import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import { Box, Button, Paper, Grid, useTheme, Typography, CircularProgress, Tooltip, Stack } from "@mui/material";
import messages from "../messages";
import { useMsal, useAccount } from "@azure/msal-react";
import { fetchMachineInfo, fetchOeeGridDateIntervalmachine, patchMachineStop } from "../../../fetchers/machine";
import { pushErrorMessage } from "../../../utils/notifications";
import { tokens } from "../../../assets/theme";
import { pushNotification } from "../../../utils/notifications";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { getOeeDataForPO } from "../../../fetchers/productionPlan";
import { fetchCompany } from "../../../fetchers/company";
import MachineGantt from '../../machineDetails/components/machineGantt';
import { getGanttDataMachine } from "../../gantt/hooks/fetchers"
import useFeatureFlags, { isFeatureEnabled } from '../../../hooks/useFeatureFlags';
import { isoFormat, formatDateFullTime, formatDateTime, formatDate, formatTime } from "../../../utils/dateTimeUtils";      
import InfoIconOutlined from '@mui/icons-material/InfoOutlined';


export default function ProductionPlanInfoDialog(props) {

    const gapInMinutes = 120;
    const defaultOee = 0.8;
    const isoFormat = "YYYY-MM-DDTHH:mm:ss";
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const language = localStorage.getItem('language') || 'en'

    //console.log("props.operation", props.operation);

    let machineId = props.operation.machineId;
    let prodOrderNo = props.operation.productionOrderId;
    //machineId = 2;
    //prodOrderNo = "260543";

    //console.log("props.operation", props.operation);

    const oeeDataPOQuery = useQuery(
        [
            "getOeeDataForPO",
            companyKey,
            prodOrderNo,
            machineId
        ],
        () =>
            getOeeDataForPO(
                companyKey,
                prodOrderNo,
                machineId
            ),
        {}
    );

    const machineInfoQuery = useQuery(
        ["machineInfo", machineId],
        () => fetchMachineInfo(machineId),
        {}
    );

    const companyQuery = useQuery(
        ["company", companyKey],
        () => fetchCompany(companyKey)
    );

    //###
    const ganttQuery = useQuery(
        ["gantt", machineId],
        () => getGanttDataMachine(machineId),
        {
            staleTime: 60000,
            refetchInterval: 60000
        }
    );

    const featuresQuery = useFeatureFlags();

    function getBoxColor(value, attributeName) {
        if (value === "") {
            return colors.primary[400];
        }

        let percentBad = 65;
        let percentWarning = 78;
        if (attributeName === "t") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getAvalabilityThresholds());
        } else if (attributeName === "a") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getPerformanceThresholds());
        } else if (attributeName === "k") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getQualityThresholds());
        } else if (attributeName === "tak") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getOeeThresholds());
        }

        if (attributeName === "a" && props.machine?.meassurePerformance === false) {
            return colors.primary[400];
        } else if (
            attributeName === "k" &&
            props.machine?.meassureQuality === false
        ) {
            return colors.primary[400];
        }
        return value > percentWarning
            ? colors.dashbordGreen[600]
            : value > percentBad && value <= percentWarning
                ? colors.yellow[500]
                : colors.redAccent[500]
    }

    function getMachineColor(machineStatus) {

        //console.log("Machine status:", machineStatus);
        switch (machineStatus) {
            case "Running":
                return colors.dashbordGreen[600];
            case "Stopped":
                return colors.redAccent[500];
            default:
                return colors.primary[400];
        }
    }

    function getSpeedUnit(unitId) {

        //console.log("Speed unit:", unitId);

        switch (unitId) {
            case 1:
                return " s";
            case 2:
                return " items/h";
            case 3:
                return " m/min";
            case 4:
                return " kg/h";
            default:
                return " s";
        }
    }

    function getAvalabilityThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        let machine = machineInfoQuery.data;
        let company = companyQuery.data;

        if (
            machine?.availabilityPercentBad !== undefined &&
            machine?.availabilityPercentBad !== null
        ) {
            percentBad = machine.availabilityPercentBad;
        } else if (
            company?.availabilityPercentBad !== undefined &&
            company?.availabilityPercentBad !== null
        ) {
            percentBad = company.availabilityPercentBad;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentBad !== null
        ) {
            percentBad = machine.oeePercentBad;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentBad = company.oeePercentBad;
        }

        if (
            machine?.availabilityPercentWarning !== undefined &&
            machine?.availabilityPercentWarning !== null
        ) {
            percentWarning = machine.availabilityPercentWarning;
        } else if (
            company?.availabilityPercentBad !== undefined &&
            company?.availabilityPercentBad !== null
        ) {
            percentWarning = company.availabilityPercentWarning;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentWarning !== null
        ) {
            percentWarning = machine.oeePercentWarning;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentWarning = company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getPerformanceThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        let machine = machineInfoQuery.data;
        let company = companyQuery.data;

        if (
            machine?.performancePercentBad !== undefined &&
            machine?.performancePercentBad !== null
        ) {
            percentBad = machine.performancePercentBad;
        } else if (
            company?.performancePercentBad !== undefined &&
            company?.performancePercentBad !== null
        ) {
            percentBad = company.performancePercentBad;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentBad !== null
        ) {
            percentBad = machine.oeePercentBad;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentBad = company.oeePercentBad;
        }

        if (
            machine?.performancePercentWarning !== undefined &&
            machine?.performancePercentWarning !== null
        ) {
            percentWarning = machine.performancePercentWarning;
        } else if (
            company?.performancePercentWarning !== undefined &&
            company?.performancePercentWarning !== null
        ) {
            percentWarning = company.performancePercentWarning;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentWarning !== null
        ) {
            percentWarning = machine.oeePercentWarning;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentWarning = company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }
    function getQualityThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        let machine = machineInfoQuery.data;
        let company = companyQuery.data;

        if (
            machine?.qualityPercentBad !== undefined &&
            machine?.qualityPercentBad !== null
        ) {
            percentBad = machine.qualityPercentBad;
        } else if (
            company?.qualityPercentBad !== undefined &&
            company?.qualityPercentBad !== null
        ) {
            percentBad = company.performancePercentBad;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentBad !== null
        ) {
            percentBad = machine.oeePercentBad;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentBad = company.oeePercentBad;
        }

        if (
            machine?.qualityPercentWarning !== undefined &&
            machine?.qualityPercentWarning !== null
        ) {
            percentWarning = machine.qualityPercentWarning;
        } else if (
            company?.qualityPercentWarning !== undefined &&
            company?.qualityPercentWarning !== null
        ) {
            percentWarning = company.qualityPercentWarning;
        } else if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentWarning !== null
        ) {
            percentWarning = machine.oeePercentWarning;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentWarning = company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getOeeThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        let machine = machineInfoQuery.data;
        let company = companyQuery.data;

        if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentBad !== null
        ) {
            percentBad = machine.oeePercentBad;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentBad = company.oeePercentBad;
        }

        if (
            machine?.oeePercentBad !== undefined &&
            machine?.oeePercentWarning !== null
        ) {
            percentWarning = machine.oeePercentWarning;
        } else if (
            company?.oeePercentBad !== undefined &&
            company?.oeePercentBad !== null
        ) {
            percentWarning = company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function splitMinutesIntoHoursAndMinutes(totalMinutes) {
        if (totalMinutes === null || totalMinutes === undefined) {
            return "";
        }
        if (totalMinutes > 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            //return formatted text
            return `${hours}h ${minutes}m`;
        }
        return `${totalMinutes}m`;
    }

    function dateDiffInMinutes(date1, date2) {
        const diffInMs = Math.abs(new Date(date2) - new Date(date1));
        return Math.floor(diffInMs / (1000 * 60));
    }

    function removeCompanyPrefix(str) {
        if (str === null || str === undefined) {
            return "";
        }
        if (str.includes("_")) {
            return str.slice(2);
        }
        return str;
    }

    // if (machineDetailsDateIntervalQuery.isLoading) {
    //     return <div>Loading...</div>;
    // }

    // console.log("machineDetailsDateIntervalQuery", machineDetailsDateIntervalQuery.data);

    if (oeeDataPOQuery.isLoading || machineInfoQuery.isLoading || companyQuery.isLoading || ganttQuery.isLoading || featuresQuery.isLoading) {
        return <Box display="flex" justifyContent="center" alignItems="center" height={"700px"}>
            <CircularProgress color="secondary" />
        </Box>;
    }
    // if (machineInfoQuery.isLoading || machineInfoQuery.isLoading || companyQuery.isLoading) {
    //     return <Box display="flex" justifyContent="center" alignItems="center" height={"700px"}>
    //         <CircularProgress color="secondary" />
    //     </Box>;
    // }
    // if (ganttQuery.isLoading || machineInfoQuery.isLoading || companyQuery.isLoading) {
    //     return <Box display="flex" justifyContent="center" alignItems="center" height={"700px"}>
    //         <CircularProgress color="secondary" />
    //     </Box>;
    // }
    // if (companyQuery.isLoading || machineInfoQuery.isLoading || companyQuery.isLoading) {
    //     return <Box display="flex" justifyContent="center" alignItems="center" height={"700px"}>
    //         <CircularProgress color="secondary" />
    //     </Box>;
    // }
    // console.log("oeeDataPOQuery", oeeDataPOQuery.data);
    // console.log("machineInfoQuery", machineInfoQuery.data[0]?.plannedQty);
    // console.log("machineInfoQuery", machineInfoQuery.data[0]);

    // const createdby = props.machineStop.commentCreatedBy != null ? "Last updated by: " + props.machineStop.commentCreatedBy : "";
    // const updatedDate = props.machineStop.commentCreatedBy != null ? formatFullDateTime(props.machineStop.updatedDate) : "";


    let t_value = oeeDataPOQuery.data?.t?.toFixed();
    let a_value = oeeDataPOQuery.data?.idalVsCurrent?.toFixed();
    let k_value = oeeDataPOQuery.data?.k?.toFixed();
    let tak_value = oeeDataPOQuery.data?.tak?.toFixed();
    t_value = (t_value === undefined) ? "" : t_value + "%";
    a_value = (a_value === undefined) ? "" : a_value + "%";
    k_value = (k_value === undefined) ? "" : k_value + "%";
    tak_value = (tak_value === undefined) ? "" : tak_value + "%";

    let idealCycleTime = oeeDataPOQuery.data?.idealCycleTime?.toFixed(2);
    let actualCycleTime = oeeDataPOQuery.data?.cycleTimes?.toFixed(2);
    //idealCycleTime = (idealCycleTime === undefined) ? "" : idealCycleTime + "s";
    //actualCycleTime = (actualCycleTime === undefined) ? "" : actualCycleTime + "s";

    let remainingQtyCalc = props.operation?.plannedQty - props.operation?.reportedQty;
    let percentageDoneCalc = ((props.operation?.reportedQty / props.operation?.plannedQty) * 100).toFixed(0) + "%";

    //calculate estimated end time based on actual cycle time and planned quantity

    //console.log("calc time", oeeDataPOQuery.data?.cycleTimes, machineInfoQuery?.data[0]?.plannedQty);

    const calcDurationInMinutes = calculateDurationInMinutes(oeeDataPOQuery.data?.cycleTimes, machineInfoQuery?.data?.[0]?.plannedQty, props.operation?.speedUnitTypeId);
    const estimatedEndTime = calculateEstimatedEndTime(calcDurationInMinutes);

    const plannedDurationInMinutes = dateDiffInMinutes(props.operation.plannedStartTime, props.operation.plannedEndTime);
    const replannedDurationInMinutes = dateDiffInMinutes(props.operation.replannedStartTime, props.operation.replannedEndTime);
    const actualDurationInMinutes = dateDiffInMinutes(oeeDataPOQuery.data?.startDate, oeeDataPOQuery.data?.endDate);

    let calculatedTotalTime = isNaN(oeeDataPOQuery.data?.uptime) ? 0 : oeeDataPOQuery.data?.uptime + (isNaN(oeeDataPOQuery.data?.downtime) ? 0 : Math.abs(oeeDataPOQuery.data?.downtime));


    function calculateDurationInMinutes(actualCycleTime, plannedQuantity, speedUnitTypeId) {
        switch (speedUnitTypeId) {
            case 0:
            case 1: // seconds per item
                if (actualCycleTime && plannedQuantity) {
                    return isNaN(actualCycleTime * plannedQuantity) ? "" : (actualCycleTime * plannedQuantity / 60).toFixed(); // Convert seconds to minutes
                }
                break;
            case 2: // items per hour
                if (actualCycleTime && plannedQuantity) {
                    return isNaN(plannedQuantity / actualCycleTime) ? "" : (plannedQuantity / actualCycleTime * 60).toFixed(); // Convert seconds to minutes
                }
                break;
            case 3: // meters per minute
                if (actualCycleTime && plannedQuantity) {
                    return isNaN(plannedQuantity / actualCycleTime) ? "" : (plannedQuantity / actualCycleTime).toFixed(); // Convert seconds to minutes
                }
                break;
            case 4: // kg per hour
                if (actualCycleTime && plannedQuantity) {
                    return isNaN(plannedQuantity / actualCycleTime) ? "" : (plannedQuantity / actualCycleTime * 60).toFixed(); // Convert seconds to minutes
                }
                break;
            default:
                return 0;
        }
        return 0;
    }

    //console.log( "calculated" , durationInMinutes);

    // function calculateEstimatedEndTime(durationInMinutes) {
    //     if (durationInMinutes > 0) {
    //         const estimatedEnd = new Date(oeeDataPOQuery.data?.startDate);
    //         estimatedEnd.setMinutes(estimatedEnd.getMinutes() + durationInMinutes);
    //         //add minutes to date
    //         estimatedEnd.setSeconds(0);
    //         return estimatedEnd;
    //     }
    //     return null;
    // }

    function calculateEstimatedEndTime(durationInMinutes) {
        if (durationInMinutes > 0) {
            let estimatedEnd = new Date(oeeDataPOQuery.data?.startDate);
            estimatedEnd.setMinutes(estimatedEnd.getMinutes() + durationInMinutes);
            return new Date(new Date(oeeDataPOQuery.data?.startDate).getTime() + durationInMinutes * 60000);
        }
        return null;
    }

    function getOEEforMachine(id) {
        let machine = props.OeeData?.find(item => item.machineid === id)
        //console.log ("machine", machine?.tak);
        return machine?.tak / 100 || defaultOee;
    }


    function calculateEndTime(speedUnitTypeId, idealCycleTime, plannedQty, reportedQty, machineId, replannedEndTime, plannedEndTime) {
        //calculated end time based on ideal speed remaing qty and OEE
        let idealTimeToEnd = 0;
        let calcEndTime = null;

        // console.log("speedUnitTypeId", item.speedUnitTypeId);  

        switch (speedUnitTypeId) {
            case 0: // Cycle time (s)
            case 1: // Cycle time (s)
                idealTimeToEnd = idealCycleTime * (plannedQty - reportedQty) / getOEEforMachine(machineId) / 60; // in minutes
                //console.log ("idealTimeToEnd", idealCycleTime, plannedQty, reportedQty, idealTimeToEnd);
                if (isNaN(idealTimeToEnd) || !isFinite(idealTimeToEnd)) {
                    idealTimeToEnd = 0;
                }
                calcEndTime = dayjs().add(idealTimeToEnd, 'minute')?.format(isoFormat);
                break;
            case 2: // Pace items/hour
                if (idealCycleTime > 0) {
                    idealTimeToEnd = (plannedQty - reportedQty) / idealCycleTime * 60 / getOEEforMachine(machineId); // in minutes
                    calcEndTime = dayjs().add(idealTimeToEnd, 'minute')?.format(isoFormat);
                } else {
                    // if idealCycleTime is 0 (not set) we cannot calculate the end time so just use the planned end time
                    ///???????
                    calcEndTime = replannedEndTime ?? plannedEndTime;
                }
                break;
            case 3: // Speed m/min
                if (idealCycleTime > 0) {
                    idealTimeToEnd = (plannedQty - reportedQty) / idealCycleTime / getOEEforMachine(machineId); // in minutes
                    calcEndTime = dayjs().add(idealTimeToEnd, 'minute')?.format(isoFormat);
                } else {
                    // if idealCycleTime is 0 (not set) we cannot calculate the end time so just use the planned end time
                    ///???????
                    calcEndTime = replannedEndTime ?? plannedEndTime;
                }
                break;
            case 4: // Speed kg/hour
                if (idealCycleTime > 0) {
                    idealTimeToEnd = (plannedQty - reportedQty) / idealCycleTime * 60 / getOEEforMachine(machineId); // in minutes
                    calcEndTime = dayjs().add(idealTimeToEnd, 'minute')?.format(isoFormat);
                } else {
                    // if idealCycleTime is 0 (not set) we cannot calculate the end time so just use the planned end time
                    ///???????
                    calcEndTime = replannedEndTime ?? plannedEndTime;
                }
                break;

            default:
                // Default case if speedUnitTypeId is not recognized
                idealTimeToEnd = idealCycleTime * (plannedQty - reportedQty) / getOEEforMachine(machineId) / 60; // in minutes
                calcEndTime = dayjs().add(idealTimeToEnd, 'minute')?.format(isoFormat);
                break;
        }

        return { "EndTime": calcEndTime, "IdealTimeToEnd": idealTimeToEnd };
    }



    let calculatedEndTime = calculateEndTime(
        props.operation?.speedUnitTypeId,
        idealCycleTime,
        props.operation?.plannedQty,
        props.operation?.reportedQty,
        props.operation?.machineId,
        props.operation?.replannedEndTime,
        props.operation?.plannedEndTime
    );
    let idealCalculatedDuration = dateDiffInMinutes(oeeDataPOQuery.data?.startDate, dayjs().add(calculatedEndTime.IdealTimeToEnd, 'minute'))


    // dayjs.add(calculatedEndTime.IdealTimeToEnd, 'minute')?.toISOString();


    //console.log ("calculatedEndTime", calculatedEndTime.EndTime, calculatedEndTime.IdealTimeToEnd);


    //console.log("oeeDataPOQuery.data", oeeDataPOQuery.data)
    //console.log("machineInfoQuery", machineInfoQuery.data)


    return (
        <>
            <Box width="auto"
                sx={{
                    p: 1,
                    m: 1
                }}>


                {/* <Box>
                    <AxionControls.AxionTextBoxMultiline
                        name="comment"
                        label={<FormattedMessage {...messages.comment} />}
                        value={machineStopPart.comment}
                        onChange={handleChange}
                    />
                </Box> */}

                {/* <Button
                name="update"
                    onClick={props.handleOpenUpdatePO}
                    value="update"
                    variant="contained"
                    color="secondary"

                /> */}

                {/* <Button
                    // disabled = {props.edit !== "true"}
                    variant="contained"
                    sx={{
                        marginBottom: 2,
                        marginLeft: "auto",
                        display: "block",
                        width: "100px",
                    }}
                    onClick={() => props.handleOpenUpdatePO(machineId, props.operation)}>
                    Update PO
                </Button> */}

                <Box
                    // height={props.operation.replannedStartTime ? "170px" : "auto"}
                    height="auto"
                    display="grid"
                    gridTemplateColumns="repeat(12, 1fr)"
                    gridAutoRows="34px"
                    gap="1px"

                    width="100%"
                    //paddingTop={"2px"}
                    // marginBottom={"2px"}
                    // paddingBottom={"2px"}
                    backgroundColor={colors.primary[700]}
                >
                           <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"left"}></Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}><FormattedMessage {...messages.startTime} /></Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}><FormattedMessage {...messages.endTime} /></Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}><FormattedMessage {...messages.duration} /></Box>
                 
                                     {/* <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" paddingLeft="50px" textAlign={"left"}>
                                         <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                             <Box width="100px"><FormattedMessage {...messages.result} /></Box>
                                             <Tooltip title={<Typography fontSize="12px">Resulting times for order, based on first and last received data.<br /> If the order is still running, endtime is current time</Typography>}>
                                                 <InfoIconOutlined /></Tooltip>
                                         </Stack>
                                     </Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatFullDateTime(oeeDataPOQuery.data?.startDate)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatFullDateTime(oeeDataPOQuery.data?.endDate)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{actualDurationInMinutes === 0 ? "" : splitMinutesIntoHoursAndMinutes(actualDurationInMinutes)}</Box>
                                      */}
                                     {props.operation.replannedStartTime &&
                                         <>
                                            <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" paddingLeft="50px" textAlign={"left"}>
                                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                                    <Box width="100px"><FormattedMessage {...messages.replanned} /></Box>
                                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.replannedInfo} /></Typography>}>
                                                        <InfoIconOutlined /></Tooltip>
                                                </Stack>
                                            </Box>
                                             <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, props.operation.replannedStartTime)}</Box>
                                             <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, props.operation.replannedEndTime)}</Box>
                                             <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{replannedDurationInMinutes === 0 ? "" : splitMinutesIntoHoursAndMinutes(replannedDurationInMinutes)}</Box>
                                         </>
                                     }
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" paddingLeft="50px" textAlign={"left"}>
                                         <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                             <Box width="100px"><FormattedMessage {...messages.planned} /></Box>
                                             <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.plannedInfo} /></Typography>}>
                                                 <InfoIconOutlined /></Tooltip>
                                         </Stack>
                                     </Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, props.operation.plannedStartTime)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, props.operation.plannedEndTime)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{plannedDurationInMinutes === 0 ? "" : splitMinutesIntoHoursAndMinutes(plannedDurationInMinutes)}</Box>
                 
                 
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" paddingLeft="50px" textAlign={"left"}>
                                         <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                             <Box width="100px"><FormattedMessage {...messages.calculated} /></Box>
                                             <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.calculatedInfo} /></Typography>}>
                                                 <InfoIconOutlined /></Tooltip>
                                         </Stack>
                                         </Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, oeeDataPOQuery.data?.startDate)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{formatDateFullTime(language, calculatedEndTime.EndTime)}</Box>
                                     <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="7px" textAlign={"center"}>{calcDurationInMinutes === 0 ? "" : splitMinutesIntoHoursAndMinutes(idealCalculatedDuration)}</Box>
                 
                </Box>

                <Box sx={{ backgroundColor: colors.primary[400], marginTop: "20px" }}>
                    <MachineGantt
                        data={ganttQuery?.data}
                        startDate={oeeDataPOQuery.data?.startDate}
                        endDate={oeeDataPOQuery.data?.endDate}
                        company={companyKey}
                        chartContainer={`chart-machinegantt`}
                        featureFlags={featuresQuery?.data}
                        divClass="machine-stop-minigantt"
                        showHeader={true}
                    //handleClick={handleMachineStopClick}
                    />
                </Box>



                <Paper
                    sx={{
                        marginTop: "20px",
                        position: "relative",
                        borderRadius: "4px",
                        backgroundColor: colors.primary[700],
                        padding: "5px",
                        // margin: "10px"
                        width: "100%",
                        height: "480px"
                    }}
                >
                    <Box
                        height="100px"
                        display="grid"
                        gridTemplateColumns="repeat(12, 1fr)"
                        gridAutoRows="90px"
                        gap="5px"
                        width="100%"

                    >

                        {/* Row 1 */}
                        <Box gridColumn="span 3" backgroundColor={getBoxColor(oeeDataPOQuery.data?.t?.toFixed(), 't')} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.a} /></Typography>
                            <Typography variant="h1">{t_value}</Typography>

                        </Box>
                        <Box gridColumn="span 3" backgroundColor={getBoxColor(oeeDataPOQuery.data?.idalVsCurrent?.toFixed(), 'a')} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.p} /></Typography>
                            <Typography variant="h1">{a_value}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={getBoxColor(oeeDataPOQuery.data?.k?.toFixed(), 'k')} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.q} /></Typography>
                            <Typography variant="h1">{k_value}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={getBoxColor(oeeDataPOQuery.data?.tak?.toFixed(), 'tak')} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.oee} /></Typography>
                            <Typography variant="h1">{tak_value}</Typography>
                        </Box>

                        {/* Row 2 */}
                        <Box gridColumn="span 3" backgroundColor={getMachineColor(machineInfoQuery?.data?.[0]?.machineStatus)} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.machine} /></Typography>
                            <Typography variant="h3">{machineInfoQuery?.data?.[0]?.machineName}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.po} /></Typography>
                            <Typography variant="h3">{props.operation?.productionOrderId}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.articleDescription} /></Typography>
                            {props.operation?.itemNo} - {props.operation?.itemName}
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.tool} /></Typography>
                            <Typography variant="h3">{props.operation?.tool}</Typography>
                        </Box>



                        {/* Row 4 */}
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.idealCT} /></Typography>
                            <Typography variant="h3">{idealCycleTime + getSpeedUnit(props.operation?.speedUnitTypeId)}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.actualCT} /></Typography>
                            <Typography variant="h3">{actualCycleTime + getSpeedUnit(props.operation?.speedUnitTypeId)}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.nrOfCycles} /></Typography>
                            <Typography variant="h3">{oeeDataPOQuery.data?.counters?.toFixed()}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.prodPerCycle} /></Typography>
                            <Typography variant="h3">{props.operation?.cavity}</Typography>
                        </Box>

                        {/* Row 5 */}

                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.scrapped} /></Typography>
                            <Typography variant="h3">{oeeDataPOQuery.data?.scrappedQty?.toFixed()}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.runtime} /></Typography>
                            <Typography variant="h3">{splitMinutesIntoHoursAndMinutes(oeeDataPOQuery.data?.uptime?.toFixed())}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.downtime} /></Typography>
                            <Typography variant="h3">{isNaN(oeeDataPOQuery.data?.downtime) ? "" : splitMinutesIntoHoursAndMinutes(Math.abs(oeeDataPOQuery.data?.downtime)?.toFixed())}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.totalTime} /></Typography>
                            <Typography variant="h3">{splitMinutesIntoHoursAndMinutes(calculatedTotalTime?.toFixed())}</Typography>
                        </Box>

                        {/* Row 6 */}
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.plannedQty} /></Typography>
                            <Typography variant="h3">{props.operation?.plannedQty?.toFixed()}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.reportedQty} /></Typography>
                            <Typography variant="h3">{props.operation?.reportedQty?.toFixed()}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.remainingQty} /></Typography>
                            <Typography variant="h3">{remainingQtyCalc}</Typography>
                        </Box>
                        <Box gridColumn="span 3" backgroundColor={colors.primary[400]} padding="10px" textAlign={"center"} >
                            <Typography variant="h5"><FormattedMessage {...messages.percentageDone} /></Typography>
                            <Typography variant="h3">{percentageDoneCalc}</Typography>
                        </Box>





                    </Box>


                </Paper>
            </Box>

        </>
    )
}